// src/oura/components/onboarding/ResendResultsDialog.tsx
//
// Results and plan, sent: send the same email again (#4469), for a client who
// did not get it. Asks first, because it lands in the client's inbox. Nothing
// else changes: the send date, the reports and the schedule stay as they are.

import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@component/dialog";
import { PillButton, TYPE } from "@oura/ui";
import { resendResults, type Onboarding } from "@service/aeo/onboarding";
import { cn } from "../../lib/cn";
import { callName, dayMonth } from "../../onboarding/derive";

export function ResendResultsDialog({ onboarding: o, onClose, onSent }: { onboarding: Onboarding; onClose: () => void; onSent: () => void }) {
    const [busy, setBusy] = useState(false);
    const [refused, setRefused] = useState<string | null>(null);
    const contact = callName(o.contact.name) || "The client";
    const last = o.resultsResentAt ?? o.resultsSentAt ?? null;

    const resend = async () => {
        setBusy(true);
        setRefused(null);
        const r = await resendResults(o.businessId);
        setBusy(false);
        if (!r.ok) {
            setRefused(r.message);
            return;
        }
        onSent();
    };

    return (
        <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
            <DialogContent className="gap-0 p-6 sm:max-w-[460px] sm:rounded-2xl">
                <DialogTitle className="pr-8 text-lg font-semibold leading-snug text-gray-900">Send the results and plan again?</DialogTitle>
                <DialogDescription asChild>
                    <div className={cn(TYPE.tb, "mt-2 space-y-2 text-gray-600")}>
                        <p className="m-0">
                            {contact} gets the same email again, with the Baseline Snapshot and the execution timeline
                            {o.contact.email ? <> at <span className="font-medium text-gray-900">{o.contact.email}</span></> : null}.
                        </p>
                        <p className="m-0 text-gray-500">
                            {last ? `Last sent ${dayMonth(last)}. ` : ""}Nothing else changes: the reports and the schedule stay as they are.
                        </p>
                        {refused && <p role="alert" className="m-0 font-medium text-rose-600">{refused}</p>}
                    </div>
                </DialogDescription>
                <div className="mt-5 flex justify-end gap-2">
                    <PillButton size="sm" disabled={busy} onClick={onClose}>Cancel</PillButton>
                    <PillButton size="sm" variant="primary" busy={busy} writes onClick={() => void resend()}>
                        {busy ? "Sending…" : "Send again"}
                    </PillButton>
                </div>
            </DialogContent>
        </Dialog>
    );
}
